export function transformWalls(walls) {
  return walls.map((wall) => {
    const { start, end } = wall;

    const adjustedStart = {
      x: start.x,
      y: start.y,
    };

    const adjustedEnd = {
      x: end.x,
      y: end.y,
    };

    return {
      points: [adjustedStart, adjustedEnd],
    };
  });
}

export function getDoorsWithLineIndex(
  doors,
  walls,
  scaleXCanvas,
  scaleYCanvas,
  offsetXCheck,
  offsetYCheck
) {
  const THRESHOLD = 10; 

  return doors?.map((door) => {
    const { x, y } = door.position;

    const wallIndex = walls.findIndex((wall) => {
      const startX = parseInt(wall.start.x);
      const startY = parseInt(wall.start.y);
      const endX = parseInt(wall.end.x);
      const endY = parseInt(wall.end.y);

      if (startY === endY) {
        return (
          Math.abs(parseInt(y) - startY) <= THRESHOLD && 
          parseInt(x) >= Math.min(startX, endX) &&
          parseInt(x) <= Math.max(startX, endX)
        );
      }

      if (startX === endX) {
        return (
          Math.abs(parseInt(x) - startX) <= THRESHOLD && 
          parseInt(y) >= Math.min(startY, endY) &&
          parseInt(y) <= Math.max(startY, endY)
        );
      }

      return false;
    });

    return {
      door_id: door.door_id,
      position: {
        x: parseInt(door?.position?.x) * scaleXCanvas + offsetXCheck,
        y: parseInt(door?.position?.y) * scaleYCanvas + offsetYCheck,
      },
      rotation: {
        x: 0,
        y: 0,
        z: door.position.rotation_radians,
      },
      width:
        parseInt(door.position.rotation_radians) === 0
          ? parseInt(door.position.height) * scaleXCanvas
          : parseInt(door.position.width) * scaleXCanvas,
      lineId: wallIndex !== -1 ? wallIndex : null, 
    };
  });
}

export function adjustDoorPositionAndOrientation(doors, vertices) {
  return doors?.map((door) => {
    const wallPoints = vertices[door.lineId]?.points;
    if (!wallPoints || wallPoints.length < 2) {
      console.error(`Invalid lineId or points for door ${door.door_id}`);
      return door; 
    }

    const startPoint = wallPoints[0];

    let adjustedPosition = { ...door.position };
    let adjustedRotation = parseFloat(door.rotation?.z || 0); 

    if (adjustedRotation === 0) {
      adjustedRotation = Math.PI / 2; 
    } else if (Math.abs(adjustedRotation) === Math.PI / 2) {
      adjustedRotation = 0; 
    }

    if (adjustedRotation === 0) {
      adjustedPosition.y = startPoint.y; 
    } else if (Math.abs(adjustedRotation) === Math.PI / 2) {
      adjustedPosition.x = startPoint.x; 
    }

    return {
      ...door,
      position: adjustedPosition, 
      rotation: {
        ...door.rotation,
        z: adjustedRotation, 
      },
    };
  });
}

export function scalePoints(vertices, scaleX, scaleY, centerX, centerY) {
  return vertices?.map((vertex) => ({
    points: vertex.points.map(({ x, y }) => ({
      x: parseInt((centerX + (x - centerX) * scaleX).toFixed(0)),
      y: parseInt((centerY + (y - centerY) * scaleY).toFixed(0)),
    })),
  }));
}

export function scaleObstacles(obstacles, scaleX, scaleY, centerX, centerY) {
  return (Array.isArray(obstacles) ? obstacles : [])
    .filter(
      (obstacle) =>
        Array.isArray(obstacle?.points) && obstacle.points.length >= 3
    )
    .map((obstacle) => ({
      points: obstacle.points.map(({ x, y }) => ({
        x: parseInt((centerX + (x - centerX) * scaleX).toFixed(0)),
        y: parseInt((centerY + (y - centerY) * scaleY).toFixed(0)),
      })),
    }));
}

export function scaleEdgeSegments(edgeSegments, scaleX, scaleY, centerX, centerY) {
  return edgeSegments?.map((edgeSegment) => ({
      end: {
        x: parseInt((centerX + (edgeSegment.end.x - centerX) * scaleX).toFixed(0)),
        y: parseInt((centerY + (edgeSegment.end.y - centerY) * scaleY).toFixed(0)),
      },
      start: {
        x: parseInt((centerX + (edgeSegment.start.x - centerX) * scaleX).toFixed(0)),
        y: parseInt((centerY + (edgeSegment.start.y - centerY) * scaleY).toFixed(0)),
      },
      type: edgeSegment.type,
  }));
}

export function scaleDoors(doors, scaleX, scaleY, centerX, centerY) {
  return doors?.map((door) => ({
    ...door,
    position: {
      x: parseInt(centerX + (door.position.x - centerX) * scaleX),
      y: parseInt(centerY + (door.position.y - centerY) * scaleY),
    },
    width: parseInt(door.width * scaleX), 
  }));
}

export function adjustFurniturePosition(
  position,
  scaleX,
  scaleY,
  centerX,
  centerY,
  isScaledToUnscaled = false
) {
  const factorX = isScaledToUnscaled ? 1 / scaleX : scaleX;
  const factorY = isScaledToUnscaled ? 1 / scaleY : scaleY;

  return {
    x: centerX + (position.x - centerX) * factorX,
    y: centerY + (position.y - centerY) * factorY,
  };
}

export const findRoom = (rooms) => {
  const getRoomDimensionsFromVertices = (vertices) => {
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

    vertices.forEach((vertex) => {
      const { x, y } = vertex; 
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });

    const width = maxX - minX; 
    const height = maxY - minY; 

    return { width, height };
  };

  const roomsWithValidDimensions = rooms.filter(
    (room) => room.width > 0 && room.height > 0
  );

  if (roomsWithValidDimensions.length > 0) {
    return roomsWithValidDimensions.reduce((bestRoom, currentRoom) => {
      const { width: currentWidth, height: currentHeight } =
        getRoomDimensionsFromVertices(currentRoom.walls);
      const { width: bestWidth, height: bestHeight } =
        getRoomDimensionsFromVertices(bestRoom.walls);

      if (currentRoom.walls.length === bestRoom.walls.length) {
        const currentArea = currentWidth * currentHeight;
        const bestArea = bestWidth * bestHeight;
        return currentArea > bestArea ? currentRoom : bestRoom;
      }

      return currentRoom.walls.length < bestRoom.walls.length
        ? currentRoom
        : bestRoom;
    });
  }

  const roomWithValidDoor = rooms?.find((room) =>
    room.doors.some((door) => {
      const width = parseInt(door.position.width, 10);
      const height = parseInt(door.position.height, 10);
      return width > 0 && height > 0;
    })
  );
  if (roomWithValidDoor) {
    return roomWithValidDoor;
  }

  return rooms[0];
};

export const doorsFindRoom = (rooms) => {
  const roomWithDoor = rooms?.find((room) =>
    room.doors.some((door) => {
      const width = parseInt(door.position.width, 10);
      const height = parseInt(door.position.height, 10);
      return width > 0 && height > 0;
    })
  );

  if (roomWithDoor) {
    return roomWithDoor;
  }

  return rooms[0];
};

export function calculateFilteredAverage(doorWidths) {
  const sortedWidths = [...doorWidths].sort((a, b) => a - b);
  const median = sortedWidths[Math.floor(sortedWidths.length / 2)];
  const absoluteDeviations = sortedWidths.map((width) =>
    Math.abs(width - median)
  );
  const mad = absoluteDeviations[Math.floor(absoluteDeviations.length / 2)];
  const threshold = 3 * mad;
  const filteredWidths = doorWidths.filter(
    (width) => Math.abs(width - median) <= threshold
  );
  const filteredAverage =
    filteredWidths.reduce((sum, width) => sum + width, 0) /
    filteredWidths.length;
  return filteredAverage;
}

export const adjustVertices = (
  vertices,
  offsetXCheck,
  offsetYCheck,
  scaleXCanvas,
  scaleYCanvas
) => {
  return vertices.map((vertex) => {
    const [startPoint, endPoint] = vertex.points;

    return {
      points: [
        {
          x: parseInt(startPoint.x * scaleXCanvas + offsetXCheck),
          y: parseInt(startPoint.y * scaleYCanvas + offsetYCheck),
        },
        {
          x: parseInt(endPoint.x * scaleXCanvas + offsetXCheck),
          y: parseInt(endPoint.y * scaleYCanvas + offsetYCheck),
        },
      ],
    };
  });
};

export function calculateScaleFactor(points, newWidth, newHeight, doorScale) {
  const allPoints = points?.flatMap((item) => item.points);

  if (!allPoints || allPoints.length === 0) {
    console.error("No points provided for scaling");
    return { scaleX: 1, scaleY: 1, centerX: 0, centerY: 0 };
  }

  const minX = Math.min(...allPoints.map((point) => point.x));
  const maxX = Math.max(...allPoints.map((point) => point.x));
  const minY = Math.min(...allPoints.map((point) => point.y));
  const maxY = Math.max(...allPoints.map((point) => point.y));

  const originalWidth = maxX - minX;
  const originalHeight = maxY - minY;


  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  const scaleX =
    newWidth > 0
      ? newWidth / originalWidth
      : 1 / (doorScale / 32);
  const scaleY =
    newHeight > 0
      ? newHeight / originalHeight
      : 1 / (doorScale / 32);

  return { scaleX, scaleY, centerX, centerY };
}

export function calculateNewScaleFactor(
  pixelLength,
  currentRealFeet,
  currentRealInches,
  newRealFeet,
  newRealInches
) {

  const newRealLengthInches = newRealFeet * 12 + newRealInches;

  const newScaleFactor = newRealLengthInches / pixelLength;

  return newScaleFactor;
}

export function convertLengthToFeetAndInches(pixelLength, scaleFactor) {
  const totalInches = pixelLength * scaleFactor;

  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches % 12);
  if (inches >= 12) { feet += 1; inches = 0; }

  return { feet, inches };
}

export const getRoomBounds = (room) => {
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;

  room.walls.forEach(({ start, end }) => {
    minX = Math.min(minX, start.x, end.x);
    maxX = Math.max(maxX, start.x, end.x);
    minY = Math.min(minY, start.y, end.y);
    maxY = Math.max(maxY, start.y, end.y);
  });

  return { minX, maxX, minY, maxY };
};

export const filterBoxesInRoom = (boxes, room) => {
  const { minX, maxX, minY, maxY } = getRoomBounds(room);
  return boxes.filter((box) => {
    const boxLeft = box.left;
    const boxRight = box.left + box.width;
    const boxTop = box.top;
    const boxBottom = box.top + box.height;

    return (
      boxLeft >= minX && boxRight <= maxX && boxTop >= minY && boxBottom <= maxY
    );
  });
};


// Comment
